(function () {
  'use strict';

  const ROOT_SELECTOR = '[data-wot-options]';
  const FORM_SELECTOR = 'form[action*="/cart/add"]';
  const CONFIG_SELECTOR = '[data-wot-options-config]';
  const INITIALIZED_ATTRIBUTE = 'data-wot-options-initialized';
  const PRICE_INITIALIZED_ATTRIBUTE = 'data-wot-price-initialized';
  const SUPPORTED_DISPLAY_TYPES = new Set([
    'checkbox',
    'checkboxmulti',
    'displaytext',
    'dropdown',
    'radio',
    'swatch',
    'swatch_multi',
  ]);
  const SUPPORTED_QUANTITY_MODES = new Set(['DEFAULT', 'EDITABLE_ABSOLUTE']);
  const DEFAULT_ERROR = 'We could not add this configured product. Please check your selections and try again.';
  const CONFIG_ERROR = 'Options for this product are temporarily unavailable. Please contact us for help.';

  function route(path) {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    return `${root}${path}`;
  }

  function asPositiveInteger(value, fallback) {
    const parsed = Number.parseInt(value, 10);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
  }

  function parseConfig(root) {
    const source = root.querySelector(CONFIG_SELECTOR);
    if (!source) throw new Error('Missing WOT option configuration.');
    const config = JSON.parse(source.textContent);
    if (!config || config.schemaVersion !== 1 || !Array.isArray(config.sets)) {
      throw new Error('Unsupported WOT option configuration.');
    }
    config.sets = config.sets.map((rawSet) => {
      const set = Object.assign({}, rawSet || {});
      const choices = Array.isArray(set.choices) ? set.choices : [];
      let setValid = set.valid !== false
        && Boolean(set.handle && set.title && set.propertyKey)
        && SUPPORTED_DISPLAY_TYPES.has(set.displayType)
        && ['A', 'B'].includes(set.tier)
        && Number(set.choicesValuesCount) === Number(set.choicesRenderedCount);
      let validDefaultCount = 0;
      let invalidChoiceCount = 0;
      let unsupportedQuantityMode = false;

      set.choices = choices.map((rawChoice) => {
        const choice = Object.assign({}, rawChoice || {});
        const hasCompanion = Boolean(choice.variantId && choice.productId);
        const tierConsistent = set.tier === 'A'
          ? choice.free === true && !hasCompanion
          // Tier B may mix free choices (no companion) with priced ones, as Bold does.
          : set.tier === 'B' && ((choice.free === true && !hasCompanion) || (choice.free === false && hasCompanion));
        const quantityModeSupported = SUPPORTED_QUANTITY_MODES.has(choice.quantityMode);
        const available = !hasCompanion || choice.available === true;
        choice.valid = choice.valid !== false
          && Boolean(choice.handle && choice.label)
          && tierConsistent
          && quantityModeSupported
          && available;
        if (!quantityModeSupported) unsupportedQuantityMode = true;
        if (!choice.valid) invalidChoiceCount += 1;
        // A default on an invalid/unavailable choice is ignored. This keeps the
        // empty/"No thanks" option selected instead of silently selecting the
        // first valid (and potentially paid) choice.
        choice.default = choice.valid && choice.default === true;
        if (choice.default) validDefaultCount += 1;
        return choice;
      });

      if (unsupportedQuantityMode) setValid = false;
      if (set.displayType !== 'displaytext' && !set.choices.some((choice) => choice.valid)) setValid = false;
      if (!set.multiple && validDefaultCount > 1) setValid = false;
      if (set.required && invalidChoiceCount) setValid = false;
      if (set.required && set.tier === 'B' && validDefaultCount !== 1) setValid = false;
      set.valid = setValid;
      return set;
    });
    if (config.sets.some((set) => set.required && !set.valid)) config.valid = false;
    return config;
  }

  function statusNode(root) {
    return root.querySelector('[data-wot-options-status]')
      || root.querySelector('[data-wot-options-error]');
  }

  function setStatus(root, message, isError) {
    const node = statusNode(root);
    if (!node) return;
    node.textContent = message || '';
    node.classList.toggle('wot-options__status--error', Boolean(isError));
    node.setAttribute('role', isError ? 'alert' : 'status');
  }

  function setPurchaseControlsDisabled(form, disabled) {
    form.querySelectorAll('[name="add"], .shopify-payment-button button').forEach((control) => {
      if (disabled) {
        control.setAttribute('disabled', 'disabled');
        control.classList.add('disabled');
      } else if (control.dataset.wotPermanentlyDisabled !== 'true') {
        control.removeAttribute('disabled');
        control.classList.remove('disabled');
      }
    });
  }

  function makeBuilderId() {
    if (!window.crypto || typeof window.crypto.getRandomValues !== 'function') {
      throw new Error('A secure cart-group identifier could not be generated.');
    }
    const words = new Uint32Array(2);
    window.crypto.getRandomValues(words);
    // FIXTURE-CONFIRM-02: derived v1 format. Two random 32-bit decimal words
    // are concatenated and truncated to a Number-safe 15-digit decimal string.
    return `${String(words[0]).padStart(10, '0')}${String(words[1]).padStart(10, '0')}`.slice(0, 15);
  }

  function choiceMap(set) {
    const map = new Map();
    (set.choices || []).forEach((choice) => {
      if (choice && choice.valid !== false && choice.handle) map.set(choice.handle, choice);
    });
    return map;
  }

  function selectedChoices(root, set) {
    if (set.displayType === 'displaytext') return [];
    const fieldset = root.querySelector(`[data-wot-option-set="${window.CSS && CSS.escape ? CSS.escape(set.handle) : set.handle}"]`);
    if (!fieldset) {
      if (set.required) throw new Error(`${set.title || 'An option'} is unavailable.`);
      return [];
    }
    const choices = choiceMap(set);
    const selected = [];

    fieldset.querySelectorAll('[data-wot-set-input]').forEach((input) => {
      if (input.tagName === 'SELECT') {
        const option = input.options[input.selectedIndex];
        if (option && option.value) {
          const choice = choices.get(option.dataset.wotChoiceHandle || '');
          if (!choice) throw new Error(`${set.title} contains an invalid selection.`);
          selected.push(choice);
        }
      } else if (input.checked) {
        const choice = choices.get(input.dataset.wotChoiceHandle || '');
        if (!choice) throw new Error(`${set.title} contains an invalid selection.`);
        selected.push(choice);
      }
    });

    if (!set.multiple && selected.length > 1) {
      throw new Error(`${set.title} has more than one selection.`);
    }
    if (set.required && selected.length === 0) {
      throw new Error(`Choose ${set.title}.`);
    }
    return selected;
  }

  function editableQuantity(root, set, choice) {
    if (choice.quantityMode !== 'EDITABLE_ABSOLUTE') return 1;
    const input = root.querySelector(`[data-wot-addon-quantity="${window.CSS && CSS.escape ? CSS.escape(set.handle) : set.handle}"]`);
    const quantity = input ? asPositiveInteger(input.value, 0) : 0;
    if (!quantity) throw new Error(`Enter a valid add-on quantity for ${set.title}.`);
    return quantity;
  }

  function readSelection(root, config) {
    const publicProperties = {};
    const priced = [];

    config.sets.forEach((set) => {
      if (set.valid === false) {
        if (set.required) throw new Error(`${set.title || 'A required option'} is unavailable.`);
        return;
      }
      if (set.displayType !== 'displaytext') publicProperties[set.propertyKey] = '';
      const choices = selectedChoices(root, set);
      if (choices.length) {
        // Live Bold v2.15.20 serializes multi-select labels as comma-joined
        // scalars with no inserted spaces. Unselected properties remain present
        // above with an empty-string value.
        publicProperties[set.propertyKey] = choices.map((choice) => choice.label).join(',');
      }

      choices.forEach((choice) => {
        if (!choice.free) {
          if (!choice.variantId || !choice.productId || choice.available !== true) {
            throw new Error(`${choice.label} is currently unavailable.`);
          }
          priced.push({
            set,
            choice,
            baseQuantity: editableQuantity(root, set, choice),
          });
        }
      });
    });

    return { publicProperties, priced };
  }

  function joinDerived(values) {
    // Live Bold v2.15.20 uses comma-joined parallel scalar arrays.
    return values.map((value) => String(value)).join(',');
  }

  function serializeVariantQuantities(values, modes) {
    if (values.length !== modes.length) {
      throw new Error('Add-on quantity mappings are misaligned.');
    }
    const slots = values.map((value, index) => {
      if (modes[index] === 'FOLLOW_MAIN') {
        if (value !== '') throw new Error('A follow-main add-on has an absolute quantity.');
        return '';
      }
      if (modes[index] !== 'EDITABLE_ABSOLUTE') {
        throw new Error('An add-on has an unsupported quantity mode.');
      }
      const quantity = asPositiveInteger(value, 0);
      if (!quantity) throw new Error('An editable add-on has an invalid quantity.');
      return String(quantity);
    });

    // The observed all-FOLLOW_MAIN Bold shape is one empty scalar. Mixed mode
    // must retain every positional slot, including a leading/trailing blank.
    return slots.some((value) => value !== '') ? joinDerived(slots) : '';
  }

  function buildItems(form, root, config) {
    const variantInput = form.querySelector('[name="id"]');
    const variantId = variantInput && asPositiveInteger(variantInput.value, 0);
    const quantityInput = form.querySelector('[name="quantity"]');
    const mainQuantity = quantityInput ? asPositiveInteger(quantityInput.value, 0) : 1;
    if (!variantId) throw new Error('Choose an available product variant.');
    if (!mainQuantity) throw new Error('Enter a valid product quantity.');

    const selection = readSelection(root, config);
    const mainProperties = Object.assign({}, selection.publicProperties);
    const items = [];

    // Bold submits every hidden schema key, using an empty string for unused
    // values. Preserve presence here; never convert unused values to zero or
    // omit them from the request.
    Object.assign(mainProperties, {
      _boldVariantNames: '',
      _boldVariantIds: '',
      _boldProductIds: '',
      _boldVariantPrices: '',
      _boldVariantQtys: '',
      _boldLineItemOneTime: '',
      _boldProductOneTime: '',
      _boldOrderOneTime: '',
      _boldBuilderId: '',
    });

    if (!selection.priced.length) {
      items.push({ id: variantId, quantity: mainQuantity, properties: mainProperties });
      return { items, mainQuantity, selection };
    }

    const builderId = makeBuilderId();
    const names = [];
    const variantIds = [];
    const productIds = [];
    const prices = [];
    const quantities = [];
    const quantityModes = [];
    const setHandles = [];
    const choiceHandles = [];

    selection.priced.forEach(({ set, choice, baseQuantity }) => {
      if (!SUPPORTED_QUANTITY_MODES.has(choice.quantityMode)) {
        throw new Error(`${set.title} has an unsupported quantity mode.`);
      }
      const quantityMode = choice.quantityMode === 'EDITABLE_ABSOLUTE' ? 'EDITABLE_ABSOLUTE' : 'FOLLOW_MAIN';
      const companionQuantity = quantityMode === 'EDITABLE_ABSOLUTE'
        ? baseQuantity
        : mainQuantity * baseQuantity;

      names.push(choice.label);
      variantIds.push(choice.variantId);
      productIds.push(choice.productId);
      prices.push(choice.priceCents);
      quantities.push(quantityMode === 'EDITABLE_ABSOLUTE' ? baseQuantity : '');
      quantityModes.push(quantityMode);
      setHandles.push(set.handle);
      choiceHandles.push(choice.handle);

      items.push({
        id: asPositiveInteger(choice.variantId, 0),
        quantity: companionQuantity,
        properties: {
          _boldBuilderId: builderId,
          _wotSetHandle: set.handle,
          _wotChoiceHandle: choice.handle,
          _wotQuantityMode: quantityMode,
          _wotBaseQuantity: String(baseQuantity),
        },
      });
    });

    Object.assign(mainProperties, {
      _boldVariantNames: joinDerived(names),
      _boldVariantIds: joinDerived(variantIds),
      _boldProductIds: joinDerived(productIds),
      _boldVariantPrices: joinDerived(prices),
      _boldVariantQtys: serializeVariantQuantities(quantities, quantityModes),
      _boldLineItemOneTime: '',
      _boldProductOneTime: '',
      _boldOrderOneTime: '',
      _boldBuilderId: builderId,
      _wotVariantQuantityModes: joinDerived(quantityModes),
      _wotSetHandles: joinDerived(setHandles),
      _wotChoiceHandles: joinDerived(choiceHandles),
      _wotSchemaVersion: '1',
    });

    items.unshift({ id: variantId, quantity: mainQuantity, properties: mainProperties });
    return { items, mainQuantity, selection };
  }

  async function responseJSON(response) {
    let payload = null;
    try {
      payload = await response.json();
    } catch (_error) {
      payload = null;
    }
    if (!response.ok) {
      const message = payload && (payload.description || payload.message || payload.status)
        ? payload.description || payload.message || payload.status
        : `Cart request failed (${response.status}).`;
      const error = new Error(String(message));
      error.status = response.status;
      error.payload = payload;
      throw error;
    }
    return payload;
  }

  async function fetchCart() {
    return responseJSON(await window.fetch(route('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    }));
  }

  async function reconcileCart() {
    try {
      const cart = await fetchCart();
      document.dispatchEvent(new CustomEvent('wot:cart:updated', { detail: { cart } }));
      window.dispatchEvent(new CustomEvent('refreshAjaxCart', { detail: { cart } }));
      return cart;
    } catch (_error) {
      return null;
    }
  }

  function formValidity(form, root, config) {
    syncRequiredCheckboxGroups(root, config);
    if (!form.checkValidity()) {
      form.reportValidity();
      return false;
    }
    try {
      readSelection(root, config);
      return true;
    } catch (error) {
      setStatus(root, error.message || DEFAULT_ERROR, true);
      return false;
    }
  }

  async function runExtendBeforeAdd(form, payload) {
    if (typeof form.wotExtendBeforeAdd !== 'function') return;
    try {
      return await form.wotExtendBeforeAdd(payload);
    } catch (error) {
      if (error && !error.extendTransaction && form.wotExtendTransaction) {
        error.extendTransaction = form.wotExtendTransaction;
      }
      throw error;
    }
  }

  async function rollbackExtend(form, transaction) {
    if (typeof form.wotExtendRollback !== 'function') return true;
    const result = await form.wotExtendRollback(transaction);
    return Boolean(result && result.verified === true);
  }

  function redirectAfterAdd(form) {
    const returnTo = form.querySelector('[name="return_to"]');
    if (returnTo && returnTo.value && returnTo.value !== 'back') {
      window.location.assign(returnTo.value);
      return true;
    }
    if (window.PXUTheme && window.PXUTheme.theme_settings
        && window.PXUTheme.theme_settings.cart_action === 'reload_page') {
      window.location.reload();
      return true;
    }
    return false;
  }

  async function submitConfiguredProduct(event, form, root, config) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (form.dataset.wotSubmitting === 'true') return;
    setStatus(root, '', false);

    if (!config.valid) {
      setStatus(root, CONFIG_ERROR, true);
      setPurchaseControlsDisabled(form, true);
      return;
    }
    if (!formValidity(form, root, config)) return;

    let payload;
    try {
      payload = buildItems(form, root, config);
    } catch (error) {
      setStatus(root, error.message || DEFAULT_ERROR, true);
      return;
    }

    form.dataset.wotSubmitting = 'true';
    setPurchaseControlsDisabled(form, true);
    setStatus(root, 'Adding your configured product…', false);

    let extendTransaction = null;
    let addCommitted = false;
    let safeToUnlock = false;
    try {
      extendTransaction = await runExtendBeforeAdd(form, payload);
      await responseJSON(await window.fetch(route('cart/add.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items: payload.items }),
      }));
      addCommitted = true;

      const cart = await reconcileCart();
      if (!cart) {
        setStatus(root, 'Your items were added, but the cart could not be refreshed. Open the cart to confirm the configured product and protection plan before adding it again.', true);
        return;
      }
      setStatus(root, 'Added to your cart.', false);
      document.dispatchEvent(new CustomEvent('wot:cart:added', {
        detail: { cart, form, itemCount: payload.items.length },
      }));
      safeToUnlock = true;
      if (redirectAfterAdd(form)) return;
    } catch (error) {
      if (addCommitted) {
        const cart = await reconcileCart();
        if (cart) {
          document.dispatchEvent(new CustomEvent('wot:cart:added', {
            detail: { cart, form, itemCount: payload.items.length },
          }));
        }
        setStatus(root, 'Your items were added, but the cart could not be confirmed. Open the cart and confirm the configured product and protection plan before adding it again.', true);
        return;
      }
      extendTransaction = (error && error.extendTransaction) || extendTransaction || form.wotExtendTransaction;
      let rollbackVerified = true;
      let manualReconcile = Boolean(error && error.extendManualReconcile);
      try {
        const rollbackResult = typeof form.wotExtendRollback === 'function'
          ? await form.wotExtendRollback(extendTransaction)
          : { verified: true, manualReconcile: false };
        rollbackVerified = Boolean(rollbackResult && rollbackResult.verified === true);
        manualReconcile = manualReconcile || Boolean(rollbackResult && rollbackResult.manualReconcile);
      } catch (_rollbackError) {
        rollbackVerified = false;
      }
      const reconciled = await reconcileCart();
      safeToUnlock = rollbackVerified && Boolean(reconciled)
        && !(error && error.extendUncertain) && !manualReconcile;
      const recovery = manualReconcile
        ? 'Manual cart review is required. Open or reload the cart and confirm the product and protection plan before trying again.'
        : (safeToUnlock
          ? 'The cart was reconciled; check it before trying again.'
          : 'The protection-plan rollback could not be verified. Open or reload the cart before trying again.');
      setStatus(root, `${error.message || DEFAULT_ERROR} ${recovery}`, true);
    } finally {
      form.dataset.wotSubmitting = 'false';
      form.wotExtendTransaction = null;
      if (config.valid && safeToUnlock) setPurchaseControlsDisabled(form, false);
    }
  }

  function syncRequiredCheckboxGroups(root, config) {
    config.sets.filter((set) => set.required && set.multiple).forEach((set) => {
      const inputs = Array.from(root.querySelectorAll(`[data-wot-set-input="${window.CSS && CSS.escape ? CSS.escape(set.handle) : set.handle}"]`))
        .filter((input) => input.type === 'checkbox' && !input.disabled);
      const anyChecked = inputs.some((input) => input.checked);
      inputs.forEach((input, index) => {
        input.required = !anyChecked && index === 0;
      });
    });
  }

  function syncEditableQuantities(root, config) {
    config.sets.forEach((set) => {
      const wrapper = root.querySelector(`[data-wot-editable-quantity][data-wot-option-set="${window.CSS && CSS.escape ? CSS.escape(set.handle) : set.handle}"]`)
        || root.querySelector(`[data-wot-option-set="${window.CSS && CSS.escape ? CSS.escape(set.handle) : set.handle}"] [data-wot-editable-quantity]`);
      if (!wrapper) return;
      let show = false;
      try {
        show = selectedChoices(root, Object.assign({}, set, { required: false }))
          .some((choice) => choice.quantityMode === 'EDITABLE_ABSOLUTE');
      } catch (_error) {
        show = false;
      }
      wrapper.hidden = !show;
      const input = wrapper.querySelector('[data-wot-addon-quantity]');
      if (input) input.required = show;
    });
  }

  function variantPriceCents(form) {
    const variantInput = form.querySelector('[name="id"]');
    const selectedOption = variantInput && variantInput.options
      ? variantInput.options[variantInput.selectedIndex]
      : null;
    if (selectedOption && selectedOption.dataset.price !== undefined) {
      const optionPrice = Number(selectedOption.dataset.price);
      if (Number.isFinite(optionPrice)) return optionPrice;
    }
    const wrapper = form.closest('[data-product-form]');
    if (!wrapper || !variantInput || !wrapper.dataset.product) return 0;
    try {
      const product = JSON.parse(wrapper.dataset.product);
      const variant = (product.variants || []).find((candidate) => String(candidate.id) === String(variantInput.value));
      return variant && Number.isFinite(Number(variant.price)) ? Number(variant.price) : 0;
    } catch (_error) {
      return 0;
    }
  }

  function updateDisplayedTotal(form) {
    const priceNode = form.querySelector('.cart-button-price');
    const button = form.querySelector('[name="add"]');
    if (!priceNode || !button || button.dataset.wotPermanentlyDisabled === 'true') return;
    const quantityInput = form.querySelector('[name="quantity"]');
    const mainQuantity = quantityInput ? asPositiveInteger(quantityInput.value, 1) : 1;
    let total = variantPriceCents(form) * mainQuantity;
    const root = form.wotOptionsRoot;
    const config = form.wotOptionsConfig;

    try {
      if (!root || !config || !config.valid) throw new Error('No configured option delta.');
      const selection = readSelection(root, Object.assign({}, config, {
        sets: config.sets.map((set) => Object.assign({}, set, { required: false })),
      }));
      selection.priced.forEach(({ choice, baseQuantity }) => {
        total += choice.priceCents * (choice.quantityMode === 'EDITABLE_ABSOLUTE'
          ? baseQuantity
          : mainQuantity * baseQuantity);
      });
    } catch (_error) {
      // A validation message belongs to submit; price falls back to base here.
    }

    const moneyFormat = form.closest('[data-product-form]')
      ? form.closest('[data-product-form]').dataset.moneyFormat
      : '${{amount}}';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      priceNode.textContent = window.Shopify.formatMoney(total, moneyFormat);
    }
    priceNode.style.display = button.disabled ? 'none' : '';
  }

  function initializeFormPrice(form) {
    if (!form || !form.querySelector('.cart-button-price')) return;
    if (!form.hasAttribute(PRICE_INITIALIZED_ATTRIBUTE)) {
      form.setAttribute(PRICE_INITIALIZED_ATTRIBUTE, 'true');
      form.addEventListener('input', () => updateDisplayedTotal(form));
      form.addEventListener('change', () => updateDisplayedTotal(form));
    }
    updateDisplayedTotal(form);
  }

  function enableOptionControls(root) {
    root.querySelectorAll('[data-wot-option-control]').forEach((control) => {
      if (control.dataset.wotUnavailable !== 'true') control.disabled = false;
    });
  }

  function initialize(root) {
    if (root.hasAttribute(INITIALIZED_ATTRIBUTE)) return;
    root.setAttribute(INITIALIZED_ATTRIBUTE, 'true');
    const form = root.closest('form');
    if (!form) return;

    let config;
    try {
      config = parseConfig(root);
    } catch (error) {
      setStatus(root, CONFIG_ERROR, true);
      setPurchaseControlsDisabled(form, true);
      return;
    }

    form.dataset.wotCoordinator = 'true';
    form.wotOptionsRoot = root;
    form.wotOptionsConfig = config;
    initializeFormPrice(form);
    if (!config.valid) {
      const button = form.querySelector('[name="add"]');
      if (button) button.dataset.wotPermanentlyDisabled = 'true';
      setPurchaseControlsDisabled(form, true);
      return;
    }

    const sync = function () {
      syncRequiredCheckboxGroups(root, config);
      syncEditableQuantities(root, config);
      updateDisplayedTotal(form);
    };
    form.addEventListener('submit', (event) => submitConfiguredProduct(event, form, root, config));
    form.addEventListener('input', sync);
    form.addEventListener('change', sync);
    enableOptionControls(root);
    sync();
    // Server markup starts every WOT purchase path disabled. Unlock only after
    // the valid coordinator owns submit and all option controls are ready.
    setPurchaseControlsDisabled(form, false);
    updateDisplayedTotal(form);
  }

  function initializeAll(scope) {
    const container = scope && scope.querySelectorAll ? scope : document;
    if (container.matches && container.matches(ROOT_SELECTOR)) initialize(container);
    container.querySelectorAll(ROOT_SELECTOR).forEach(initialize);
    if (container.matches && container.matches(FORM_SELECTOR)) initializeFormPrice(container);
    container.querySelectorAll(FORM_SELECTOR).forEach(initializeFormPrice);
  }

  function boot() {
    initializeAll(document);
    if (document.body && window.MutationObserver) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) initializeAll(node);
          });
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
    document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
    if (window.InstantClick && typeof window.InstantClick.on === 'function') {
      window.InstantClick.on('change', () => initializeAll(document));
    }
  }

  window.WOTOptions = Object.assign(window.WOTOptions || {}, {
    buildItems,
    initializeAll,
    makeBuilderId,
    readSelection,
    updateDisplayedTotal,
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
